import { api } from './client';
import { longRequest } from './requestTimeout';
import type { components } from './generated/schema';

/**
 * WEB-1654 — причины, по которым перевод числится отставшим. Зеркало
 * backend/src/translations/enums/translation-stale-reason.enum.ts; расхождение
 * ловит сторож в frontend/src/utils/translationStaleReason.test.ts (он читает
 * ИСХОДНИК серверного перечисления, а не копию).
 */
export type TranslationStaleReason =
  | 'no_balance'
  | 'model_error'
  | 'partial_response'
  | 'source_changed'
  /**
   * WEB-1863 (решение владельца 02.09): перевод сделан с ДРУГОГО исходного
   * языка, чем стоит у воронки/экрана цен сейчас. Единственная причина, при
   * которой перевод посетителю ПО-ПРЕЖНЕМУ показывается, — гасить его не за
   * что, решение о повторном переводе принимает владелец.
   */
  | 'source_language_changed';

export interface QuizTranslationDto {
  id: string;
  quizId: string;
  language: string;
  content: object[];
  sourceQuizId: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * WEB-1497/WEB-1400: перевод отстал от оригинала ЛИБО пришёл от модели
   * неполным. В обоих случаях он не считается актуальным: публичная страница
   * его не показывает (посетитель видит цельный исходный язык, а не смесь), а
   * в списке языков рядом стоит пометка «переведено частично». Поле
   * необязательное — ответы, сериализованные до WEB-1400, его не несут.
   */
  isStale?: boolean;
  /**
   * WEB-1654: ПОЧЕМУ перевод отстал. Пусто/нет поля = причина неизвестна
   * (строка отстала до миграции 1900000000204 либо ответ старого сервера) —
   * кабинет тогда показывает прежний общий текст.
   */
  staleReason?: TranslationStaleReason | null;
  /**
   * WEB-1652: адреса строк, которых модель не вернула. Окно правки
   * подсвечивает по ним поля — иначе человек видит текст на исходном языке и
   * не знает, что именно система считает недобранным.
   */
  missingPaths?: string[] | null;
  /**
   * WEB-1863: язык, С КОТОРОГО сделан этот перевод. Расходится с языком
   * записи, если исходный язык переставили уже после перевода — из этой пары
   * кабинет собирает предупреждение «испанская версия переведена с русского,
   * а сейчас оригинал английский». Пусто/нет поля = строка старше миграции
   * 1900000000237 либо ответ старого сервера; тогда языки не называем.
   *
   * Поле описано здесь руками (как и соседние): сгенерированная схема
   * `api/generated/schema.ts` его получит следующей перегенерацией типов.
   */
  sourceLanguage?: string | null;
  /**
   * WEB-1653: отпечаток содержимого. Окно правки запоминает его при открытии
   * и возвращает при сохранении; разошёлся — сервер отказывает 409 вместо
   * того, чтобы затереть работу догона.
   */
  contentVersion?: string;
  /**
   * WEB-2230: перевод сделан с черновика ни разу не публиковавшегося ресурса,
   * а в черновике с тех пор появились новые или изменённые строки текста —
   * догон первой публикации их переведёт и оплатит (только их). Считает
   * сервер (`isDraftAheadOfTranslation`); нет поля (старый сервер) — пометки нет.
   */
  draftAheadOfTranslation?: boolean;
}

export interface PaywallTranslationDto {
  id: string;
  paywallId: string;
  language: string;
  content: object[];
  sourcePaywallId: string | null;
  createdAt: string;
  updatedAt: string;
  /** WEB-1497/WEB-1400 — см. QuizTranslationDto.isStale. */
  isStale?: boolean;
  /**
   * WEB-1654: ПОЧЕМУ перевод отстал. Пусто/нет поля = причина неизвестна
   * (строка отстала до миграции 1900000000204 либо ответ старого сервера) —
   * кабинет тогда показывает прежний общий текст.
   */
  staleReason?: TranslationStaleReason | null;
  /**
   * WEB-1652: адреса строк, которых модель не вернула. Окно правки
   * подсвечивает по ним поля — иначе человек видит текст на исходном языке и
   * не знает, что именно система считает недобранным.
   */
  missingPaths?: string[] | null;
  /**
   * WEB-1863: язык, С КОТОРОГО сделан этот перевод. Расходится с языком
   * записи, если исходный язык переставили уже после перевода — из этой пары
   * кабинет собирает предупреждение «испанская версия переведена с русского,
   * а сейчас оригинал английский». Пусто/нет поля = строка старше миграции
   * 1900000000237 либо ответ старого сервера; тогда языки не называем.
   *
   * Поле описано здесь руками (как и соседние): сгенерированная схема
   * `api/generated/schema.ts` его получит следующей перегенерацией типов.
   */
  sourceLanguage?: string | null;
  /**
   * WEB-1653: отпечаток содержимого. Окно правки запоминает его при открытии
   * и возвращает при сохранении; разошёлся — сервер отказывает 409 вместо
   * того, чтобы затереть работу догона.
   */
  contentVersion?: string;
  /**
   * WEB-2230: перевод сделан с черновика ни разу не публиковавшегося ресурса,
   * а в черновике с тех пор появились новые или изменённые строки текста —
   * догон первой публикации их переведёт и оплатит (только их). Считает
   * сервер (`isDraftAheadOfTranslation`); нет поля (старый сервер) — пометки нет.
   */
  draftAheadOfTranslation?: boolean;
}

/**
 * WEB-2847 — прогноз пачки переводов ДО запуска (`GET …/translate-batch/estimate`):
 * языки, которые примет отправка, сумма, которую отложит резерв (центы =
 * кредиты), свободный остаток кошелька организации проекта и замер времени.
 * Окно языков показывает эти числа, а не свою формулу.
 */
export type TranslationBatchEstimateDto =
  components['schemas']['TranslationBatchEstimateResponseDto'];
/** WEB-114 — ответ отправки пачки (202): языки в очереди и состояние по языку. */
export type TranslationBatchStartDto =
  components['schemas']['TranslationBatchStartResponseDto'];
/** WEB-114 п.4 — состояние одного языка пачки. */
export type TranslationBatchJobDto =
  components['schemas']['TranslationBatchLanguageStateDto'];

export const translationsApi = {
  // Quiz translations
  // WEB-2824: `model` — поле модели из одной двери `modelIdForRequest`
  // (utils/aiModelSelection), кладётся в тело как есть; пустой — тело как было.
  translateQuiz: (
    projectId: string,
    quizId: string,
    targetLanguage: string,
    model: { modelId?: string } = {},
  ) =>
    api.post(
      `/projects/${projectId}/quizzes/${quizId}/translate`,
      { targetLanguage, ...model },
      longRequest('quizTranslate'),
    ),

  listQuizTranslations: (projectId: string, quizId: string) =>
    api.get(`/projects/${projectId}/quizzes/${quizId}/translations`),

  updateQuizTranslation: (
    projectId: string,
    quizId: string,
    lang: string,
    content: object[],
    /**
     * WEB-1653: отпечаток содержимого, снятый окном правки при ОТКРЫТИИ.
     * Сервер сверяет его с текущим и отказывает 409, если пока окно было
     * открыто содержимое переписал догон, — иначе оплаченная работа модели
     * молча терялась бы под целым содержимым окна.
     */
    baseContentVersion?: string,
  ) =>
    api.patch(`/projects/${projectId}/quizzes/${quizId}/translations/${lang}`, {
      content,
      baseContentVersion,
    }),

  deleteQuizTranslation: (projectId: string, quizId: string, lang: string) =>
    api.delete(`/projects/${projectId}/quizzes/${quizId}/translations/${lang}`),

  // Paywall translations
  // WEB-2824: `model` — поле модели из одной двери `modelIdForRequest`
  // (utils/aiModelSelection), кладётся в тело как есть; пустой — тело как было.
  translatePaywall: (
    projectId: string,
    paywallId: string,
    targetLanguage: string,
    model: { modelId?: string } = {},
  ) =>
    api.post(
      `/projects/${projectId}/paywalls/${paywallId}/translate`,
      { targetLanguage, ...model },
      longRequest('paywallTranslate'),
    ),

  listPaywallTranslations: (projectId: string, paywallId: string) =>
    api.get(`/projects/${projectId}/paywalls/${paywallId}/translations`),

  updatePaywallTranslation: (
    projectId: string,
    paywallId: string,
    lang: string,
    content: object[],
    /** WEB-1653 — см. updateQuizTranslation. */
    baseContentVersion?: string,
  ) =>
    api.patch(`/projects/${projectId}/paywalls/${paywallId}/translations/${lang}`, {
      content,
      baseContentVersion,
    }),

  deletePaywallTranslation: (projectId: string, paywallId: string, lang: string) =>
    api.delete(`/projects/${projectId}/paywalls/${paywallId}/translations/${lang}`),
  // WEB-2847: «перевести на выбранные языки» — прогноз и одна отправка пачки.
  estimateQuizBatch: (projectId: string, quizId: string, languages: string[]) =>
    api.get<TranslationBatchEstimateDto>(
      `/projects/${projectId}/quizzes/${quizId}/translate-batch/estimate`,
      { params: { languages: languages.join(',') } },
    ),

  translateQuizBatch: (projectId: string, quizId: string, targetLanguages: string[]) =>
    api.post<TranslationBatchStartDto>(
      `/projects/${projectId}/quizzes/${quizId}/translate-batch`,
      { targetLanguages },
    ),

  estimatePaywallBatch: (projectId: string, paywallId: string, languages: string[]) =>
    api.get<TranslationBatchEstimateDto>(
      `/projects/${projectId}/paywalls/${paywallId}/translate-batch/estimate`,
      { params: { languages: languages.join(',') } },
    ),

  translatePaywallBatch: (projectId: string, paywallId: string, targetLanguages: string[]) =>
    api.post<TranslationBatchStartDto>(
      `/projects/${projectId}/paywalls/${paywallId}/translate-batch`,
      { targetLanguages },
    ),
};

